// ---------------------------------------------------------------------------
// Repository ของทุกโมดูล CRM
// ---------------------------------------------------------------------------
// ปลายทางข้อมูลมี 2 โหมด สลับอัตโนมัติ:
//   1. Google Sheets (Apps Script)  — เมื่อตั้ง REACT_APP_GS_URL ไว้  ← โหมดหลัก
//   2. localStorage                — เมื่อไม่ได้ตั้ง (ใช้เดโมออฟไลน์)
//
// หน้าเว็บเรียกผ่าน repository เท่านั้น ไม่ต้องรู้ว่าข้อมูลอยู่ที่ไหน
// ---------------------------------------------------------------------------
import { table, delay, paginate, resetAll as resetLocal, exportScope, seedAll } from "./store";
import { gsEnabled, gsGet, gsPost, sheetOf, GLOBAL_COLLECTIONS } from "../services/gsClient";

const GLOBAL = "global";

// factory สร้าง repository มาตรฐาน 1 collection
const makeRepo = (collection, { searchKeys = [], scope = null } = {}) => {
  const isGlobal = !!scope || GLOBAL_COLLECTIONS.includes(collection);
  const scopeOf = (orgId) => scope || orgId;
  const sheet = sheetOf(collection);
  const orgParam = (orgId) => (isGlobal ? undefined : orgId);

  return {
    collection,
    sheet,

    // ------- อ่านทั้งหมด (กรองฝั่ง client ด้วย filterFn ได้) -------
    all: async (orgId, filterFn) => {
      if (gsEnabled()) {
        const result = await gsGet("list", { table: sheet, org: orgParam(orgId) });
        const rows = result?.rows || [];
        return filterFn ? rows.filter(filterFn) : rows;
      }
      return delay(() => table(scopeOf(orgId), collection).list(filterFn));
    },

    allSync: (orgId, filterFn) => table(scopeOf(orgId), collection).list(filterFn),

    // ------- อ่านแบบแบ่งหน้า -------
    list: async (orgId, params = {}) => {
      const { filter, ...rest } = params;

      if (gsEnabled()) {
        // มี filter ที่เป็นฟังก์ชัน -> ดึงมาแล้วกรอง/แบ่งหน้าฝั่ง client
        if (filter) {
          const result = await gsGet("list", { table: sheet, org: orgParam(orgId) });
          const rows = (result?.rows || []).filter(filter);
          return paginate(rows, { searchKeys, ...rest });
        }
        const result = await gsGet("list", {
          table: sheet,
          org: orgParam(orgId),
          page: rest.page,
          perPage: rest.perPage,
          search: rest.search,
          searchKeys: searchKeys.join(","),
          sortBy: rest.sortBy,
          sortDir: rest.sortDir,
        });
        return result || { rows: [], total: 0, page: 1, perPage: 10 };
      }

      const rows = table(scopeOf(orgId), collection).list(filter);
      return delay(() => paginate(rows, { searchKeys, ...rest }));
    },

    get: async (orgId, id) => {
      // ส่ง org ไปด้วยเพื่อกันดึงแถวข้ามร้านด้วยการเดา id
      if (gsEnabled()) return gsGet("get", { table: sheet, id, org: orgParam(orgId) });
      return delay(() => table(scopeOf(orgId), collection).find(id));
    },

    create: async (orgId, payload) => {
      if (gsEnabled()) return gsPost("create", { table: sheet, org: orgParam(orgId), data: payload });
      return delay(() => table(scopeOf(orgId), collection).create(payload, collection.slice(0, 3)));
    },

    update: async (orgId, id, payload) => {
      if (gsEnabled()) return gsPost("update", { table: sheet, id, data: payload });
      return delay(() => table(scopeOf(orgId), collection).update(id, payload));
    },

    remove: async (orgId, id) => {
      if (gsEnabled()) return gsPost("remove", { table: sheet, id });
      return delay(() => table(scopeOf(orgId), collection).remove(id));
    },

    replaceAll: async (orgId, rows) => {
      if (gsEnabled()) return gsPost("replace", { table: sheet, org: orgParam(orgId), rows });
      return delay(() => table(scopeOf(orgId), collection).save(rows) || rows);
    },
  };
};

// --------------------------------------------------------------------------
// global scope
// --------------------------------------------------------------------------
export const organizationRepo = makeRepo("organizations", { scope: GLOBAL, searchKeys: ["name", "nameEn"] });
export const subscriptionRepo = makeRepo("subscriptions", { scope: GLOBAL, searchKeys: ["orgName", "plan", "refNo", "transactionId"] });
const baseRoleRepo = makeRepo("roles", { scope: GLOBAL, searchKeys: ["name"] });
export const userRepo = makeRepo("users", { scope: GLOBAL, searchKeys: ["email", "name"] });

// --------------------------------------------------------------------------
// บทบาท & สิทธิ์ — แยกต่อร้าน
// --------------------------------------------------------------------------
// ชีต/ตาราง roles เดิมเป็นแบบ global (ไม่มี org_id) ใช้ร่วมกันทุกร้าน จึงไม่ลบ/ไม่แก้ของเดิม:
//   - แถวเดิมที่ isSystem (เช่น เจ้าของธุรกิจ)  -> ยังใช้ร่วมทุกร้านแบบอ่านอย่างเดียว
//   - แถวเดิมที่ไม่ใช่ system                  -> ถือเป็น "แม่แบบ" ตอนร้านเปิดใช้ครั้งแรกจะ copy
//                                                  เป็นแถวของร้าน (org_id + templateId) แล้วแก้ได้อิสระ
// ร้านแก้สิทธิ์ได้เฉพาะแถวของตัวเอง ร้านอื่นไม่เห็นและแก้ไม่ได้
const roleSeeding = {}; // orgId -> Promise กัน copy ซ้ำเมื่อหลายหน้าเรียกพร้อมกัน

/** คืน { globals, mine } — globals = แถวเดิมที่ไม่มี org_id, mine = แถวของร้านนี้เท่านั้น */
const readRoleRows = async (orgId) => {
  if (!gsEnabled()) {
    return { globals: table(GLOBAL, "roles").all(), mine: table(orgId, "roles").all() };
  }
  const result = await gsGet("list", { table: sheetOf("roles"), org: orgId });
  const rows = result?.rows || [];
  // กรองเองอีกชั้น เผื่อ Code.gs ยังเป็นเวอร์ชันเก่าที่ไม่กรอง roles ตามร้าน (ห้ามเห็นของร้านอื่น)
  return {
    globals: rows.filter((r) => !r.org_id),
    mine: rows.filter((r) => String(r.org_id) === String(orgId)),
  };
};

const seedOrgRoles = async (orgId) => {
  const { globals, mine } = await readRoleRows(orgId);
  const templates = globals.filter((r) => !r.isSystem && !mine.some((m) => m.templateId === r.id));

  for (const tpl of templates) {
    const { id, org_id, created_at, updated_at, createdAt, updatedAt, ...rest } = tpl; // eslint-disable-line no-unused-vars
    const data = { ...rest, templateId: id, isSystem: false };
    if (gsEnabled()) await gsPost("create", { table: sheetOf("roles"), org: orgId, data });
    else table(orgId, "roles").create(data, "rol");
  }
};

const ensureOrgRoles = (orgId) => {
  if (!roleSeeding[orgId]) {
    roleSeeding[orgId] = seedOrgRoles(orgId).finally(() => {
      delete roleSeeding[orgId]; // เก็บเฉพาะที่กำลังทำอยู่ ไม่จำผลเก่า
    });
  }
  return roleSeeding[orgId];
};

export const roleRepo = {
  ...baseRoleRepo,

  // all(orgId) -> บทบาทของร้านนั้น ; all() ไม่ส่ง orgId -> เฉพาะแถว global เดิม (ไม่คืนของร้านใดเลย)
  all: async (orgId, filterFn) => {
    if (!orgId) {
      const rows = (await baseRoleRepo.all(undefined)).filter((r) => !r.org_id);
      return filterFn ? rows.filter(filterFn) : rows;
    }
    return roleRepo.forOrg(orgId).then((rows) => (filterFn ? rows.filter(filterFn) : rows));
  },

  /** บทบาทที่ร้านนี้ใช้ได้ = บทบาท system ที่ใช้ร่วม (อ่านอย่างเดียว) + บทบาทของร้านเอง */
  forOrg: async (orgId) => {
    if (!orgId) return [];
    await ensureOrgRoles(orgId);
    const { globals, mine } = await readRoleRows(orgId);
    const shared = globals.filter((r) => r.isSystem).map((r) => ({ ...r, shared: true }));
    return [...shared, ...mine.map((r) => ({ ...r, isSystem: false }))];
  },

  /** บันทึกสิทธิ์ของบทบาทของร้าน — แก้บทบาท system / ของร้านอื่นไม่ได้ */
  savePermissions: async (orgId, roleId, permissions) => {
    if (!orgId) throw new Error("ไม่พบร้านที่จะบันทึกสิทธิ์");
    const list = await roleRepo.forOrg(orgId);
    const role = list.find((r) => r.id === roleId);
    if (!role) throw new Error("ไม่พบบทบาทนี้ในร้าน");
    if (role.isSystem) throw new Error("บทบาทระบบแก้ไขไม่ได้");
    const next = Array.from(new Set(permissions));
    if (gsEnabled()) return gsPost("update", { table: sheetOf("roles"), id: roleId, org: orgId, data: { permissions: next } });
    return delay(() => table(orgId, "roles").update(roleId, { permissions: next }));
  },
};

export const NOTIFICATION_EVENTS = [
  { key: "booking_created", name: "มีการจองใหม่", enabled: true },
  { key: "payment_received", name: "ได้รับชำระเงิน", enabled: true },
  { key: "slip_pending", name: "มีสลิปรอตรวจสอบ", enabled: true },
  { key: "installment_due", name: "ค่างวดใกล้ครบกำหนด", enabled: true },
  { key: "stock_low", name: "สต็อกต่ำกว่ากำหนด", enabled: false },
  { key: "contract_expiring", name: "สัญญาใกล้หมดอายุ", enabled: false },
];

// รวมค่าที่เก็บไว้เข้ากับรายการเหตุการณ์มาตรฐาน (เหตุการณ์ใหม่โผล่เอง) และทิ้ง lineToken/lineEnabled
const normalizeNotification = (stored) => {
  const saved = Array.isArray(stored?.events) ? stored.events : [];
  const known = NOTIFICATION_EVENTS.map((e) => {
    const hit = saved.find((s) => s.key === e.key);
    return { ...e, enabled: hit ? !!hit.enabled : e.enabled };
  });
  const extra = saved.filter((s) => s.key && !NOTIFICATION_EVENTS.some((e) => e.key === s.key)).map((s) => ({ key: s.key, name: s.name || s.key, enabled: !!s.enabled }));
  return { events: [...known, ...extra] };
};

export const settingRepo = {
  getPermissionGroups: async () => {
    if (gsEnabled()) {
      const result = await gsGet("list", { table: "permission_groups" });
      return result?.rows || [];
    }
    return delay(() => table(GLOBAL, "permissionGroups").all());
  },

  getNotificationSettings: async () => {
    if (gsEnabled()) {
      const result = await gsGet("list", { table: "settings", where: { key: "notification" } });
      const row = (result?.rows || [])[0];
      return row?.value || { lineToken: "", lineEnabled: true, events: [] };
    }
    return delay(() => table(GLOBAL, "notificationSettings").all());
  },

  saveNotificationSettings: async (payload) => {
    if (gsEnabled()) {
      const result = await gsGet("list", { table: "settings", where: { key: "notification" } });
      const row = (result?.rows || [])[0];
      if (row) return gsPost("update", { table: "settings", id: row.id, data: { value: payload } });
      return gsPost("create", { table: "settings", data: { key: "notification", value: payload } });
    }
    return delay(() => {
      table(GLOBAL, "notificationSettings").save(payload);
      return payload;
    });
  },


  // ------- การแจ้งเตือน: แยกต่อร้าน (แถว settings = { key:"notification", org_id, value }) -------
  // เก็บเฉพาะ "เปิด/ปิดแจ้งเตือนแต่ละประเภท" — LINE Notify ปิดบริการแล้ว จึงไม่เก็บ/ไม่ส่ง token
  // ร้านที่ยังไม่เคยบันทึก จะเริ่มจากค่าที่เคยตั้งไว้แบบ global เดิม (อ่านอย่างเดียว ไม่แก้/ไม่ลบ) หรือค่าเริ่มต้น
  getOrgNotificationSettings: async (orgId) => {
    if (!orgId) return normalizeNotification(null);
    if (gsEnabled()) {
      const result = await gsGet("list", { table: "settings", org: orgId, where: { key: "notification" } });
      const rows = result?.rows || [];
      const own = rows.find((r) => String(r.org_id) === String(orgId));
      const legacy = rows.find((r) => !r.org_id);
      return normalizeNotification((own || legacy)?.value);
    }
    return delay(() => {
      const own = table(orgId, "orgSettings").list((r) => r.key === "notification")[0];
      if (own) return normalizeNotification(own.value);
      const legacy = table(GLOBAL, "notificationSettings").all();
      return normalizeNotification(Array.isArray(legacy) ? null : legacy);
    });
  },

  saveOrgNotificationSettings: async (orgId, payload) => {
    if (!orgId) throw new Error("ไม่พบร้านที่จะบันทึกการตั้งค่า");
    const value = normalizeNotification(payload);
    if (gsEnabled()) {
      const result = await gsGet("list", { table: "settings", org: orgId, where: { key: "notification" } });
      const own = (result?.rows || []).find((r) => String(r.org_id) === String(orgId));
      if (own) await gsPost("update", { table: "settings", id: own.id, org: orgId, data: { value } });
      else await gsPost("create", { table: "settings", org: orgId, data: { key: "notification", value } });
      return value;
    }
    return delay(() => {
      const t = table(orgId, "orgSettings");
      const own = t.list((r) => r.key === "notification")[0];
      if (own) t.update(own.id, { value });
      else t.create({ key: "notification", value }, "set");
      return value;
    });
  },

  // ตั้งค่าแยกต่อร้าน (เช่น online_payment) — แถวในชีต settings = { key, org_id, value }
  // key ไม่ซ้ำกับ "notification" และกรองด้วย org_id จึงไม่ชนกับการตั้งค่าแจ้งเตือนเดิม
  getOrgSetting: async (orgId, key, fallback = {}) => {
    if (!orgId) return fallback;
    if (gsEnabled()) {
      const result = await gsGet("list", { table: "settings", where: { key, org_id: orgId } });
      const row = (result?.rows || [])[0];
      return row?.value ? { ...fallback, ...row.value } : fallback;
    }
    return delay(() => {
      const row = table(orgId, "orgSettings").list((r) => r.key === key)[0];
      return row?.value ? { ...fallback, ...row.value } : fallback;
    });
  },

  saveOrgSetting: async (orgId, key, value) => {
    if (!orgId) throw new Error("ไม่พบร้านที่จะบันทึกการตั้งค่า");
    if (gsEnabled()) {
      const result = await gsGet("list", { table: "settings", where: { key, org_id: orgId } });
      const row = (result?.rows || [])[0];
      if (row) await gsPost("update", { table: "settings", id: row.id, data: { value } });
      else await gsPost("create", { table: "settings", org: orgId, data: { key, value } });
      return value;
    }
    return delay(() => {
      const t = table(orgId, "orgSettings");
      const row = t.list((r) => r.key === key)[0];
      if (row) t.update(row.id, { value });
      else t.create({ key, value }, "set");
      return value;
    });
  },
};

// --------------------------------------------------------------------------
// org scope
// --------------------------------------------------------------------------
export const branchRepo = makeRepo("branches", { searchKeys: ["name", "phone", "address"] });
export const staffRepo = makeRepo("staff", { searchKeys: ["name", "position", "phone", "email"] });
export const customerRepo = makeRepo("customers", { searchKeys: ["name", "code", "phone", "email", "lineId"] });
export const leadRepo = makeRepo("leads", { searchKeys: ["name", "phone", "interestedIn", "source"] });
export const productRepo = makeRepo("products", { searchKeys: ["name", "code", "category"] });
export const resourceRepo = makeRepo("resources", { searchKeys: ["name", "type"] });
export const assetRepo = makeRepo("assets", { searchKeys: ["name", "code", "category"] });
export const inventoryRepo = makeRepo("inventory", { searchKeys: ["productName"] });
export const stockMoveRepo = makeRepo("stockMoves", { searchKeys: ["productName", "reason"] });
export const appointmentRepo = makeRepo("appointments", { searchKeys: ["code", "customerName", "serviceName"] });
export const tierRepo = makeRepo("tiers", { searchKeys: ["name"] });
export const rewardRepo = makeRepo("rewards", { searchKeys: ["name"] });
export const pointRepo = makeRepo("pointTransactions", { searchKeys: ["customerName", "detail"] });
export const packageRepo = makeRepo("customerPackages", { searchKeys: ["customerName", "packageName"] });
export const chatThreadRepo = makeRepo("chatThreads", { searchKeys: ["customerName", "lastMessage"] });
export const chatMessageRepo = makeRepo("chatMessages", { searchKeys: ["text"] });
export const slipRepo = makeRepo("slips", { searchKeys: ["customerName", "bank", "matchedInvoiceNo"] });
export const campaignRepo = makeRepo("campaigns", { searchKeys: ["name", "segment"] });
export const salesOrderRepo = makeRepo("salesOrders", { searchKeys: ["orderNo", "customerName", "trackingNo"] });
export const posSaleRepo = makeRepo("posSales", { searchKeys: ["billNo"] });
export const quotationRepo = makeRepo("quotations", { searchKeys: ["docNo", "customerName"] });
export const invoiceRepo = makeRepo("invoices", { searchKeys: ["docNo", "customerName", "customerPhone"] });
export const receiptRepo = makeRepo("receipts", { searchKeys: ["docNo", "customerName", "invoiceNo"] });
export const paymentLinkRepo = makeRepo("paymentLinks", { searchKeys: ["code", "title", "customerName"] });
export const contractRepo = makeRepo("contracts", { searchKeys: ["docNo", "title", "customerName"] });
export const installmentRepo = makeRepo("installmentPlans", { searchKeys: ["contractNo", "customerName"] });
export const commissionRepo = makeRepo("commissions", { searchKeys: ["staffName", "refDoc", "period"] });
export const fieldJobRepo = makeRepo("fieldJobs", { searchKeys: ["jobNo", "customerName", "type"] });
export const documentRepo = makeRepo("documents", { searchKeys: ["name", "customerName"] });
export const notificationRepo = makeRepo("notifications", { searchKeys: ["title", "detail"] });
export const activityRepo = makeRepo("activities", { searchKeys: ["title", "detail", "customerName"] });
export const walletRepo = makeRepo("wallets");
export const faqRepo = makeRepo("faqs", { searchKeys: ["question", "answer", "category"] });
export const transactionLogRepo = makeRepo("transactions", { searchKeys: ["refNo", "transactionId", "customerName"] });
export const boostOrderRepo = makeRepo("boostOrders", { searchKeys: ["orderNo", "targetUrl", "platform"] });
export const boostPointRepo = makeRepo("boostPoints", { searchKeys: ["detail", "refNo", "orderNo"] });
export const addonPurchaseRepo = makeRepo("addonPurchases", { searchKeys: ["name", "refNo", "transactionId"] });
export const customRequestRepo = makeRepo("customRequests", { searchKeys: ["requestNo", "title", "status"] });
export const taxDocumentRepo = makeRepo("taxDocuments", { searchKeys: ["docNo", "buyerName", "invoiceNo", "refDocNo"] });

// --------------------------------------------------------------------------
// helper สำหรับหน้า dashboard
// --------------------------------------------------------------------------
/** filter สำหรับ repo.list/all — กรองตามสาขา (ไม่ส่ง branchId = ไม่กรอง) เช่น slipRepo.all(orgId, branchFilter(branchId)) */
export const branchFilter = (branchId) => (row) => !branchId || row.branchId === branchId;

const sum = (rows, key) => rows.reduce((total, row) => total + (Number(row[key]) || 0), 0);
const sameDay = (a, b) => String(a).slice(0, 10) === String(b).slice(0, 10);

/** ดึงหลายตารางในครั้งเดียว (โหมด GS ใช้ action=bulk เพื่อลด request) */
const loadTables = async (orgId, collections) => {
  if (gsEnabled()) {
    const sheets = collections.map(sheetOf);
    const result = await gsGet("bulk", { tables: sheets.join(","), org: orgId });
    return collections.reduce((acc, name, index) => {
      acc[name] = result[sheets[index]] || [];
      return acc;
    }, {});
  }
  return collections.reduce((acc, name) => {
    acc[name] = table(orgId, name).all();
    return acc;
  }, {});
};

export const dashboardRepo = {
  summary: async (orgId, branchId) => {
    const data = await loadTables(orgId, [
      "invoices", "appointments", "customers", "leads", "salesOrders", "posSales", "slips", "installmentPlans", "inventory",
    ]);

    const inBranch = (row) => !branchId || row.branchId === branchId;
    const invoices = data.invoices.filter(inBranch);
    const appointments = data.appointments.filter(inBranch);
    const customers = data.customers.filter(inBranch);
    const leads = data.leads.filter(inBranch);
    const salesOrders = data.salesOrders.filter(inBranch);
    const posSales = data.posSales.filter(inBranch);
    const installments = data.installmentPlans.filter(inBranch);
    const inventory = data.inventory.filter(inBranch);
    const slips = data.slips.filter((row) => !branchId || !row.branchId || row.branchId === branchId); // สลิปเก่าที่ไม่มี branchId นับทุกสาขา

    const today = new Date().toISOString();
    const paidInvoices = invoices.filter((inv) => inv.status === "paid");

    const revenueByMonth = Array.from({ length: 12 }).map((_, index) => {
      const date = new Date();
      date.setMonth(date.getMonth() - (11 - index));
      const label = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
      const amount = paidInvoices
        .filter((inv) => String(inv.paidAt || inv.createdAt).slice(0, 7) === label)
        .reduce((total, inv) => total + Number(inv.total || 0), 0);
      return { label, amount };
    });

    const channelCount = customers.reduce((acc, customer) => {
      if (!customer.channel) return acc;
      acc[customer.channel] = (acc[customer.channel] || 0) + 1;
      return acc;
    }, {});

    const parseSchedule = (plan) => (Array.isArray(plan.schedule) ? plan.schedule : []);

    return {
      revenueTotal: sum(paidInvoices, "total"),
      revenueThisMonth: paidInvoices
        .filter((inv) => String(inv.paidAt || "").slice(0, 7) === today.slice(0, 7))
        .reduce((total, inv) => total + Number(inv.total || 0), 0),
      outstanding: sum(invoices.filter((inv) => inv.status !== "paid"), "total"),
      overdueCount: invoices.filter((inv) => inv.status === "overdue").length,
      customerTotal: customers.length,
      newCustomerThisMonth: customers.filter((c) => String(c.createdAt).slice(0, 7) === today.slice(0, 7)).length,
      appointmentToday: appointments.filter((a) => sameDay(a.startAt, today)).length,
      appointmentUpcoming: appointments.filter((a) => new Date(a.startAt) > new Date() && a.status !== "cancelled").length,
      leadOpen: leads.filter((l) => !["won", "lost"].includes(l.stage)).length,
      leadValue: sum(leads.filter((l) => !["won", "lost"].includes(l.stage)), "value"),
      orderPending: salesOrders.filter((o) => o.shippingStatus === "pending" || o.shippingStatus === "packing").length,
      posToday: posSales.filter((s) => sameDay(s.createdAt, today)).reduce((total, s) => total + Number(s.total || 0), 0),
      slipPending: slips.filter((s) => s.status === "pending").length,
      installmentOverdue: installments.reduce(
        (total, plan) => total + parseSchedule(plan).filter((s) => s.status === "overdue").length,
        0
      ),
      lowStock: inventory.filter((item) => Number(item.quantity) <= Number(item.reorderPoint)).length,
      revenueByMonth,
      channelCount,
      todayAppointments: appointments
        .filter((a) => sameDay(a.startAt, today))
        .sort((a, b) => new Date(a.startAt) - new Date(b.startAt))
        .slice(0, 8),
      recentInvoices: [...invoices].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 6),
    };
  },

  customerSummary: async (orgId, customerId) => {
    const data = await loadTables(orgId, [
      "invoices", "appointments", "customerPackages", "contracts", "installmentPlans", "wallets", "activities",
    ]);

    const mine = (rows) => rows.filter((row) => row.customerId === customerId);
    const invoices = mine(data.invoices);
    const appointments = mine(data.appointments);
    const installments = mine(data.installmentPlans);

    return {
      invoices,
      appointments,
      packages: mine(data.customerPackages),
      contracts: mine(data.contracts),
      installments,
      wallet: mine(data.wallets)[0] || null,
      activities: mine(data.activities),
      unpaidTotal: sum(invoices.filter((inv) => inv.status !== "paid"), "total"),
      nextAppointment:
        appointments
          .filter((a) => new Date(a.startAt) > new Date() && a.status !== "cancelled")
          .sort((a, b) => new Date(a.startAt) - new Date(b.startAt))[0] || null,
      nextInstallment:
        installments
          .flatMap((plan) =>
            (Array.isArray(plan.schedule) ? plan.schedule : [])
              .filter((s) => s.status !== "paid")
              .map((s) => ({ ...s, contractNo: plan.contractNo }))
          )
          .sort((a, b) => new Date(a.dueAt) - new Date(b.dueAt))[0] || null,
    };
  },
};

// --------------------------------------------------------------------------
// ซิงก์ข้อมูลตัวอย่างขึ้น Google Sheets
// --------------------------------------------------------------------------
/** ดันข้อมูล seed ทั้งชุด (ทุกองค์กร) ขึ้นชีต — ใช้ตอนติดตั้งครั้งแรก */
export const syncSeedToGs = async () => {
  if (!gsEnabled()) throw new Error("ยังไม่ได้ตั้งค่า REACT_APP_GS_URL");

  const { buildSeed } = await import("./seed");
  const seed = buildSeed();
  const payload = {};

  Object.entries(seed).forEach(([scope, collections]) => {
    Object.entries(collections).forEach(([collection, rows]) => {
      if (!Array.isArray(rows)) {
        // ค่าที่ไม่ใช่ array (เช่น notificationSettings) เก็บลงชีต settings
        payload.settings = payload.settings || [];
        payload.settings.push({ id: `set_${collection}`, key: collection, value: rows });
        return;
      }
      const sheet = sheetOf(collection);
      const withOrg = rows.map((row) => (scope === "global" ? row : { org_id: scope, ...row }));
      payload[sheet] = (payload[sheet] || []).concat(withOrg);
    });
  });

  return gsPost("import", { payload });
};

export const resetAll = async (keepUsers = true) => {
  if (gsEnabled()) return gsPost("reset", { keepUsers });
  return resetLocal();
};

export { exportScope, seedAll };
